import React, { useContext } from 'react';
import { Alert, Box, Button, FormField, Input, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { buildColumnDef } from 'src/components/segment-cdt/create/segmentCreateUtils';
import SegmentGlPullDataSource from 'src/components/segment-cdt/create/SegmentGlPullDataSource';
import GlPullCriteriaFields from 'src/components/segment-cdt/create/GlPullCriteriaFields';

/**
 * "GL Balance Pull" column config. Captures what the contract stores for a PULL_GL column: displayName +
 * currency (dataType is fixed NUMBER) and the pullCriteria.
 *
 * Composed of three parts rather than one: this form owns the column name and the commit, the scalar pull
 * settings (period, currency, FX type, FX date, description) come from {@link GlPullCriteriaFields} so the
 * derived-balance form offers exactly the same set, and the ledger plus the per-COA-dimension multi-value data
 * keys (with custom rollups) come from the reused {@link SegmentGlPullDataSource}.
 */
export default function GlPullColumnForm({ hideCancel = false }: { hideCancel?: boolean }) {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    const editor = cw.columnEditor;
    const draft = editor?.draft ?? {};
    const pc = draft.pullCriteria ?? {};
    // The refusal for this draft, held beside it in the reducer so SET_COLUMN_DRAFT drops it on any edit.
    const localError = editor?.error ?? '';

    // Read here only for the commit-time membership check below; the field itself lives in
    // GlPullCriteriaFields, which loads the same list. Both calls hit one cached fetch shape (the hook skips an
    // empty query and the server ignores the value), so the duplicate hook costs a second request at most and
    // keeps the check independent of the child having rendered.
    const [currencyOptions] = services.customDataTablesService.getCurrencyCodeLOV('load');
    const setDraft = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload(patch));
    const setCriteria = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload({ pullCriteria: patch }));

    const onAdd = () => {
        // Duplicate/reference checks span the union of the table's existing columns (empty in the create
        // wizard, populated when adding columns to an existing table) and the queued columns.
        const union = [...(state.detail.definition?.columns ?? []), ...cw.columns];
        const others = union.map((c) => c.displayName || '');
        const otherIds = union.map((c) => c.columnId).filter((id): id is string => !!id);
        // Only enforce currency-membership once the LOV has loaded; an empty list means "cannot check".
        const validCurrencies = (currencyOptions ?? []).length ? [CONSTANTS.STAT_CURRENCY, ...(currencyOptions ?? [])] : [];
        const { column, error } = buildColumnDef(draft, others, otherIds, validCurrencies, union);
        if (error || !column) {
            dispatch(ACTIONS.SET_COLUMN_ERROR.withPayload(error || 'Invalid column.'));
            return;
        }
        dispatch(ACTIONS.COMMIT_COLUMN.withPayload(column));
    };

    return (
        <SpaceBetween size="l">
            <FormField label="Column name" description="Enter a unique name for this column.">
                <Input
                    value={draft.displayName ?? ''}
                    placeholder="e.g. 2D Revenue"
                    onChange={({ detail }) => setDraft({ displayName: detail.value })}
                />
            </FormField>

            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <img src={CONSTANTS.IMAGE_PATH + 'segment-gl-pull.svg'} width={28} height={28} alt="" style={{ display: 'block' }} />
                <Box variant="h4" padding={{ top: 'n', bottom: 'n' }}>Configure data source: GL Balance Pull</Box>
            </SpaceBetween>

            <GlPullCriteriaFields
                criteria={pc}
                currency={draft.currency}
                onCriteriaChange={setCriteria}
                onCurrencyChange={(currency) => setDraft({ currency })}
            />

            <SegmentGlPullDataSource value={pc} onChange={(patch) => setCriteria(patch)} />

            {localError && <Alert type="error">{localError}</Alert>}

            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    {!hideCancel && <Button onClick={() => dispatch(ACTIONS.CANCEL_COLUMN_EDITOR)}>Cancel</Button>}
                    <Button variant="primary" onClick={onAdd}>
                        Add column
                    </Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
